import { usePlayPickems, useRecentPickems } from '@/api/query/usePickem';
import { isPickemHeaderVisible } from '@low6-pickem/style-config';

export interface DefaultHeaderStyle {
  headerLogoUrl?: string;
  headerBgGradientStart?: string;
  headerBgGradientEnd?: string;
  headerBgImageUrl?: string;
  headerBgImageUrlMobile?: string;
  /** Whether the lobby header banner should render (DKPLAT-437). */
  isHeaderVisible: boolean;
  /**
   * True while the source pickem lists are still loading. Consumers should hold
   * off rendering a fallback logo until this is false, to avoid a flash of the
   * wrong-brand default logo before the configured banner resolves (DKPLAT-446).
   */
  isLoading: boolean;
}

type PickemWithStyle = {
  is_default_style?: boolean;
  style_config?: Record<string, unknown> | null;
};

const headerOf = (p?: PickemWithStyle): Record<string, unknown> | undefined =>
  (p?.style_config?.header as Record<string, unknown> | undefined) ?? undefined;

/**
 * Derives the app-header styling for the list/landing screens (DKPLAT-446).
 *
 * The pickem detail screen styles its header from the pickem being viewed
 * (CompetitionView wraps AppHeader in AppHeaderStyleProvider + passes
 * style_config.header.logo). The landing/lobby has no single pickem in context,
 * so without this it fell back to AppHeader's default `/logo.svg` (the wrong,
 * Skypicks, banner). Here we source the header from the tenant's designated
 * default-style pickem (`is_default_style`), falling back to the first pickem
 * that carries a header logo — so the landing shows the banner configured in
 * the styling config, matching the detail screen.
 */
export function useDefaultHeaderStyle(): DefaultHeaderStyle {
  const play = usePlayPickems();
  const recent = useRecentPickems();

  const pickems = [
    ...((play.data?.pickems as PickemWithStyle[] | undefined) ?? []),
    ...((recent.data?.pickems as PickemWithStyle[] | undefined) ?? []),
  ];

  // Prefer the tenant's default-style pickem; fall back to the first with header content.
  const source =
    pickems.find(p => p.is_default_style && isPickemHeaderVisible(headerOf(p))) ??
    pickems.find(p => isPickemHeaderVisible(headerOf(p)));

  const header = headerOf(source);

  return {
    headerLogoUrl: header?.logo as string | undefined,
    headerBgGradientStart: header?.gradient_start as string | undefined,
    headerBgGradientEnd: header?.gradient_end as string | undefined,
    headerBgImageUrl: header?.background_image as string | undefined,
    headerBgImageUrlMobile: header?.background_image_mobile as string | undefined,
    isHeaderVisible: isPickemHeaderVisible(header),
    // Use isPending (not isLoading): these queries are disabled until UserContext
    // is ready, and a disabled query reports isLoading=false despite having no
    // data yet. isPending stays true until data actually resolves, so we keep
    // the fallback logo suppressed across the disabled-waiting window (DKPLAT-446).
    isLoading: play.isPending || recent.isPending,
  };
}
